import PrintIcon from "@mui/icons-material/Print";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  MenuItem,
  Modal,
  Paper,
  Select,
  SelectChangeEvent,
  Slide,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import React, { useEffect, useState } from "react";
import { brandColors } from "../../App";
import {
  fetchStoreList,
  submitRequest,
  transferList,
  updateRequest,
} from "../../services/inventoryService";
import { printRequest } from "../../components/Pdf/printPdf";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<unknown>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const StoreHistory: React.FC = () => {
  const initialFilter = { status: "PENDING", page: 1, limit: 10 };
  const [filter, setFilter] = useState<any>(initialFilter);
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedProducts, setSelectedProducts] = useState<
    { product_id: string; name: string; qty: number; saved: boolean }[]
  >([]);
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [expandedSubCategories, setExpandedSubCategories] = useState<string[]>(
    [],
  );
  const [selectedStore, setSelectedStore] = useState<any>();
  const [stores, setStores] = useState<{ label: string; value: string }[]>([]);
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [page, setPage] = useState(2);
  const [rows, setRows] = useState<any[]>([]);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportData, setExportData] = useState<{
    payload: any[];
    notes?: string;
  } | null>(null);

  const rearrangeData = (data: any[]): any => {
    if (!data || data.length === 0) {
      setRows([]);
      return;
    }

    const grouped: Record<string, any> = {};

    data.forEach((item: any) => {
      const invoiceNo = item.invoice_no;

      if (!grouped[invoiceNo]) {
        grouped[invoiceNo] = {
          invoice_no: invoiceNo,
          status: item.status,
          created_at: item.created_at, // first/latest row already sorted
          sending_store: item.sending_store,
          receiving_store: item.receiving_store,
          store: item.store,
          total_qty_packed: 0,
          items: [],
        };
      }

      grouped[invoiceNo].items.push(item);
      grouped[invoiceNo].total_qty_packed += Number(item.qty_packed || 0);
    });

    // Convert object to array
    return Object.values(grouped);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    // setPage(newPage);
    setFilter({ ...filter, page: newPage });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    // setRowsPerPage(parseInt(event.target.value, 10));
    // setPage(0);
    setFilter({ ...filter, page: 0, limit: parseInt(event.target.value, 10) });
  };

  const handleRowClick = (invoice_no: string) => {
    setExpandedCategories(
      (prev) =>
        prev.includes(invoice_no)
          ? prev.filter((c) => c !== invoice_no) // collapse
          : [...prev, invoice_no], // expand
    );
  };

  const getWarehose = async () => {
    try {
      const data = await fetchStoreList(""); // Fetch the inventory data from an API
      setStores(
        data.map((item: any) => ({ label: item.name, value: item.store_id })),
      );
    } catch (error) {
      console.error("Error fetching inventory:", error);
    } finally {
      setLoading(false);
    }
  };

  const getTransferList = async (filter: any) => {
    try {
      const response = await transferList(filter); // Fetch the inventory data from an API
      if (response?.data?.data.length > 0) {
        setProducts(response.data.data);
        setRows(rearrangeData(response.data.data));
        setFilter({
          ...filter,
          limit: response.data.limit,
          count: response.data.count,
          page: response.data.page,
          total: response.data.total,
        });
      }
    } catch (error) {
      console.error("Error fetching inventory:", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    getWarehose();
    getTransferList(filter);
  }, [filter.status, filter.store_id]);

  const handleChangeStatus = (event: SelectChangeEvent) => {
    setFilter({ ...filter, status: event.target.value, page: 1 });
    const tempStore = stores.find((s) => s.value === event.target.value);
    setSelectedStore({
      label: tempStore?.label as string,
      value: tempStore?.value as string,
    });
  };

  const handleSubmit = async () => {
    try {
      const payload = selectedProducts.filter(
        (p) => p.saved === true && p.qty > 0,
      );
      const response = await submitRequest(payload, selectedStore?.value);
      if (response.status === 200) {
        alert("Request submitted successfully");
        setSelectedProducts([]);
        setSelectedStore(null);
        handleClose();
      }
    } catch (error) {
      alert("Something went wrong while submitting the request.");
      console.log("Something went wrong while submitting the request.", error);
    }
  };
  const handlePrev = async () => {
    if (filter.page === 1) return;
    setFilter({ ...filter, page: filter.page - 1 });
    getTransferList({ ...filter, page: filter.page - 1 });
  };

  const handleNext = async () => {
    const totalPages = Math.ceil(filter.count / filter.limit);
    if (filter.page !== totalPages) {
      setFilter({ ...filter, page: filter.page + 1 });
      getTransferList({ ...filter, page: filter.page + 1 });
    }
  };
  const handleChangeStore = (event: SelectChangeEvent) => {
    // setSelectedStore(event.target.value as string);
    const tempStore = stores.find((s) => s.value === event.target.value);
    setFilter({ ...filter, store_id: event.target.value });
    setSelectedStore({
      label: tempStore?.label as string,
      value: tempStore?.value as string,
    });
  };
  const handleQuantityChange = (
    value: number,
    product: { product_id: string; name: string },
  ) => {
    setProducts((prev) => {
      const existing = prev.find((p) => p.product_id === product.product_id);
      return prev.map((p) =>
        p.product_id === product.product_id ? { ...p, qty_packed: value } : p,
      );
    });
  };
  const handleUpdate = async (item: any) => {
    try {
      const response = await updateRequest(item);
      if (response) {
        alert("Request updated successfully");
        setSelectedProducts([]);
        setSelectedStore(null);
        handleClose();
      }
    } catch (error) {
      alert("Something went wrong while submitting the request.");
      console.log("Something went wrong while submitting the request.", error);
    }
  };
  const handlePrint = async (item: any) => {
    try {
      const payload = item.items;
      const response = await printRequest(payload, item.notes);
      // if (response) {
      //   alert("Request printed successfully");
      //   setSelectedProducts([]);
      //   setSelectedStore(null);
      //   handleClose();
      // }
    } catch (error) {
      alert("Something went wrong while submitting the request.");
      console.log("Something went wrong while submitting the request.", error);
    }
  };
  return (
    <>
      <Box sx={{ padding: 1 }}>
        {loading ? (
          <CircularProgress sx={{ display: "block", margin: "0 auto" }} />
        ) : (
          <>
            <TableContainer component={Paper} sx={{ height: "80vh" }}>
              <Table
                size="small"
                sx={{ minWidth: 650 }}
                aria-label="simple table"
              >
                <TableHead className="table-header">
                  <TableRow sx={{ border: "1px solid #132A13" }}>
                    <TableCell
                      sx={{
                        backgroundColor: brandColors.header,
                        color: "white",
                      }}
                      align="right"
                    >
                      No
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: brandColors.header,
                        color: "white",
                      }}
                      align="center"
                    >
                      Store
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: brandColors.header,
                        color: "white",
                      }}
                      align="center"
                    >
                      Invoice No
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: brandColors.header,
                        color: "white",
                      }}
                      align="center"
                    >
                      Requested Date
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: brandColors.header,
                        color: "white",
                      }}
                      align="center"
                    >
                      Print
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows?.map((row: any, index: number) => (
                    <>
                      <TableRow
                        key={index}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                        onClick={() => handleRowClick(row.invoice_no)}
                        style={{ cursor: "pointer" }}
                      >
                        <TableCell align="right">{index + 1}</TableCell>
                        <TableCell align="center">{row.store}</TableCell>
                        <TableCell align="center">{row.invoice_no}</TableCell>
                        <TableCell align="center">
                          {row.created_at.split("T")[0]} (
                          {new Date(row.created_at).toLocaleTimeString()})
                        </TableCell>
                        <TableCell
                          align="center"
                          onClick={() => handlePrint(row)}
                        >
                          <PrintIcon />
                        </TableCell>
                      </TableRow>
                      {expandedCategories.includes(row.invoice_no) && (
                        <TableRow>
                          <TableCell colSpan={5}>
                            <Collapse
                              in={expandedCategories.includes(row.invoice_no)}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Table
                                size="small"
                                style={{ backgroundColor: "ButtonShadow" }}
                              >
                                <TableHead>
                                  <TableRow>
                                    <TableCell
                                      sx={{
                                        backgroundColor: "GrayText",
                                        color: "white",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      No
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        backgroundColor: "GrayText",
                                        color: "white",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      Product
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        backgroundColor: "GrayText",
                                        color: "white",
                                        fontWeight: "bold",
                                      }}
                                      align="center"
                                    >
                                      Quantity
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        backgroundColor: "GrayText",
                                        color: "white",
                                        fontWeight: "bold",
                                      }}
                                      align="center"
                                    >
                                      Unpacked
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {row.items?.map(
                                    (product: any, idx: number) => (
                                      <TableRow key={product.product_id}>
                                        <TableCell>{idx + 1}</TableCell>
                                        <TableCell>{product.product}</TableCell>
                                        <TableCell align="center">
                                          {product.qty_packed}
                                        </TableCell>
                                        <TableCell align="center">
                                          {product.qty_unpacked}
                                        </TableCell>
                                      </TableRow>
                                    ),
                                  )}
                                </TableBody>
                              </Table>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}
            >
              <div></div>
              <div></div>
              <div></div>
              {/* <TablePagination
                component="div"
                count={filter.count}
                page={filter.page}
                onPageChange={handleChangePage}
                rowsPerPage={filter.limit}
                onRowsPerPageChange={handleChangeRowsPerPage}
              /> */}
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handlePrev}
                  disabled={filter.page === 1}
                >
                  {`<-`}
                </Button>
                {`${filter.page} of ${Math.ceil(filter.count / filter.limit)}`}
                {/* <Button variant="contained" color="primary" disabled>
                </Button> */}
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleNext}
                  disabled={filter.page === filter.total}
                >
                  {`->`}
                </Button>
              </div>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Select
                  labelId="Store"
                  name="Store"
                  id="Store"
                  //   value={selectedStore?.value}
                  label="Warehouse"
                  sx={{ width: "200px" }}
                  onChange={handleChangeStore}
                >
                  {stores.map((store) => (
                    <MenuItem value={store.value} key={store.value}>
                      {store.label}
                    </MenuItem>
                  ))}
                </Select>
                <Select
                  labelId="Status"
                  name="Status"
                  id="Status"
                  value={filter.status}
                  label="Warehouse"
                  sx={{ width: "200px" }}
                  onChange={handleChangeStatus}
                >
                  <MenuItem value={`APPROVED`} key={`Approved`}>
                    Approved
                  </MenuItem>
                  <MenuItem value={`PENDING`} key={`Pending`}>
                    Pending
                  </MenuItem>
                </Select>
              </div>
            </Box>
          </>
        )}
      </Box>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Verify list
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: brandColors.header }}>
                <TableCell>Product</TableCell>
                <TableCell>Quantity</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedProducts.map((item) => (
                <TableRow key={item.product_id}>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.qty}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Button
            variant="contained"
            color="primary"
            sx={{ mt: 2 }}
            onClick={handleSubmit}
          >
            Submit
          </Button>
        </Box>
      </Modal>
    </>
  );
};

export default StoreHistory;
